import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactElement } from 'react'
import type { Pos } from '@games/core'
import { COLS, ROWS, cellKey } from './types'
import type { BoardPresentation, Color, Piece } from './types'
import { LABELS } from './labels'
import { genMoves } from './rules'
import { DEFAULT_METRICS, boardSize, cellCenter, cellFromPoint, fitCell, flipRC } from './geometry'
import type { BoardMetrics } from './geometry'

export interface BoardProps {
  readonly presentation: BoardPresentation
  readonly disabled: boolean
  readonly onMove: (from: Pos, to: Pos) => void
}

const pieceStyle = (piece: Piece, x: number, y: number, cell: number, isSelected: boolean): CSSProperties => ({
  position: 'absolute',
  left: x,
  top: y,
  width: cell * 0.82,
  height: cell * 0.82,
  transform: 'translate(-50%, -50%)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: cell * 0.42,
  fontWeight: 'bold',
  border: '2px solid #333',
  background: piece.color === 'r' ? '#fdf1d0' : '#f0f0f0',
  color: piece.color === 'r' ? '#c0392b' : '#1a1a1a',
  outline: isSelected ? '4px solid #ffd54f' : undefined,
  boxShadow: isSelected ? '0 0 12px 2px rgba(255, 213, 79, 0.9)' : '0 2px 4px rgba(0, 0, 0, 0.5)',
  zIndex: isSelected ? 2 : 1,
  pointerEvents: 'none',
})

const fogStyle = (x: number, y: number, cell: number): CSSProperties => ({
  position: 'absolute',
  left: x,
  top: y,
  width: cell,
  height: cell,
  transform: 'translate(-50%, -50%)',
  background:
    'repeating-linear-gradient(45deg, rgba(20, 20, 25, 0.55), rgba(20, 20, 25, 0.55) 6px, rgba(40, 40, 48, 0.55) 6px, rgba(40, 40, 48, 0.55) 12px)',
  pointerEvents: 'none',
})

/**
 * 遮罩的绘制顺序：**先深灰（黑方看不到）铺满，再红色（红方看不到）半透明叠上层**（D8）。
 * 双方都看不到的格子会依次画上两层，于是「红灰都在」自有一套可辨认的观感。
 */
const MASK_PAINT_ORDER = ['b', 'r'] as const

const maskStyle = (color: Color, x: number, y: number, cell: number): CSSProperties => ({
  position: 'absolute',
  left: x,
  top: y,
  width: cell,
  height: cell,
  transform: 'translate(-50%, -50%)',
  background: color === 'r' ? 'rgba(214, 48, 49, 0.5)' : 'rgba(44, 48, 54, 0.78)',
  pointerEvents: 'none',
  // 盖在棋子之上（D1：上帝视角要看得见双方棋子），但在可点击的命中层之下
  zIndex: 2,
})

const markStyle = (x: number, y: number, cell: number, isCapture: boolean): CSSProperties => ({
  position: 'absolute',
  left: x,
  top: y,
  width: isCapture ? cell * 0.82 : cell * 0.3,
  height: isCapture ? cell * 0.82 : cell * 0.3,
  transform: 'translate(-50%, -50%)',
  borderRadius: '50%',
  background: isCapture ? 'transparent' : 'rgba(46, 204, 113, 0.85)',
  border: isCapture ? '3px solid rgba(231, 76, 60, 0.9)' : undefined,
  pointerEvents: 'none',
})

const lastMoveStyle = (x: number, y: number, cell: number): CSSProperties => ({
  position: 'absolute',
  left: x,
  top: y,
  width: cell * 0.93,
  height: cell * 0.93,
  boxSizing: 'border-box',
  transform: 'translate(-50%, -50%)',
  border: '3px solid rgba(255, 213, 79, 0.9)',
  borderRadius: 8,
  pointerEvents: 'none',
})

export function Board({ presentation, disabled, onMove }: BoardProps): ReactElement {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [cell, setCell] = useState(DEFAULT_METRICS.cell)
  const [selected, setSelected] = useState<Pos | null>(null)

  // 旧版 updateBoardSize()：容器尺寸变化时按宽高较小值重算格距
  useEffect(() => {
    const element = wrapperRef.current
    if (!element) return
    const update = (): void => {
      const availW = element.clientWidth
      const availH = element.clientHeight
      if (availW <= 0 || availH <= 0) return
      setCell(fitCell(availW, availH, DEFAULT_METRICS))
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // 外部状态更新（新的一步棋 / 重开 / 切换观察模式）后，本地选中失效
  useEffect(() => {
    setSelected(null)
  }, [presentation])

  const visibleSet = presentation.visible
  const gameOver = presentation.winner !== null
  const locked = disabled || presentation.self === null

  /** 终局与上帝视角（`visible === null`）都揭示全部格子 */
  const revealed = (key: string): boolean =>
    gameOver || visibleSet === null || visibleSet.has(key)

  const targets = useMemo(
    () =>
      selected
        ? genMoves(presentation.board, selected.r, selected.c).filter((m) => !m.visionOnly)
        : [],
    [presentation.board, selected],
  )

  const metrics: BoardMetrics = useMemo(
    () => ({ rows: ROWS, cols: COLS, cell, pad: Math.round(cell * 0.56) }),
    [cell],
  )
  const size = boardSize(metrics)

  const handleCell = (r: number, c: number): void => {
    const self = presentation.self
    if (locked || self === null) return
    const piece = presentation.board[r][c]
    if (selected) {
      if (selected.r === r && selected.c === c) {
        setSelected(null)
        return
      }
      if (piece && piece.color === self) {
        setSelected({ r, c })
        return
      }
      if (targets.some((m) => m.r === r && m.c === c)) {
        const from = selected
        setSelected(null)
        onMove(from, { r, c })
        return
      }
      // 点到空白格或非法的己方区域：取消选中（T2 §4.11）
      setSelected(null)
      return
    }
    if (piece && piece.color === self) setSelected({ r, c })
  }

  const handleBoardClick = (event: ReactMouseEvent<HTMLDivElement>): void => {
    const rect = event.currentTarget.getBoundingClientRect()
    const point = cellFromPoint(
      event.clientX - rect.left,
      event.clientY - rect.top,
      metrics,
      presentation.flip,
    )
    if (point) handleCell(point.r, point.c)
  }

  /** 观众视角：这个格子被谁看不到（拼进无障碍描述，界面也有图例说明） */
  const maskNotes = (key: string): string => {
    const notes = presentation.masks
      .filter((mask) => mask.cells.has(key))
      .map((mask) => `${mask.color === 'r' ? '红方' : '黑方'}看不到`)
    return notes.length === 0 ? '' : `（${notes.join('，')}）`
  }

  const describeCell = (r: number, c: number): string => {
    const key = cellKey(r, c)
    const piece = presentation.board[r][c]
    const shown = piece !== null && (revealed(key) || piece.color === presentation.self)
    const { r: sr, c: sc } = flipRC(r, c, presentation.flip)
    const prefix =
      shown && piece
        ? `${piece.color === 'r' ? '红' : '黑'}${LABELS[piece.color][piece.type]}`
        : revealed(key)
          ? '空'
          : '迷雾'
    const position = `第${sr + 1}行第${sc + 1}列`
    if (selected && selected.r === r && selected.c === c) return `${prefix} ${position} 已选中`
    if (targets.some((m) => m.r === r && m.c === c)) return `${prefix} ${position} 可走`
    return `${prefix} ${position}${maskNotes(key)}`
  }

  const x = (c: number): number => metrics.pad + c * cell
  const y = (r: number): number => metrics.pad + r * cell

  const layers: ReactElement[] = []
  const hitCells: ReactElement[] = []

  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const key = cellKey(r, c)
      const piece = presentation.board[r][c]
      const center = cellCenter(r, c, metrics, presentation.flip)
      const shown = piece !== null && (revealed(key) || piece.color === presentation.self)

      if (piece && shown) {
        const isSelected = selected !== null && selected.r === r && selected.c === c
        layers.push(
          <div key={`piece-${key}`} style={pieceStyle(piece, center.x, center.y, cell, isSelected)}>
            {LABELS[piece.color][piece.type]}
          </div>,
        )
      } else if (!revealed(key)) {
        layers.push(<div key={`fog-${key}`} style={fogStyle(center.x, center.y, cell)} />)
      }

      hitCells.push(
        <div
          key={`cell-${key}`}
          role="gridcell"
          tabIndex={locked ? -1 : 0}
          aria-label={describeCell(r, c)}
          style={{
            position: 'absolute',
            left: center.x,
            top: center.y,
            width: cell,
            height: cell,
            transform: 'translate(-50%, -50%)',
            background: 'transparent',
            cursor: locked ? 'default' : 'pointer',
            zIndex: 3,
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              handleCell(r, c)
            }
          }}
        />,
      )
    }
  }

  // 遮罩层：按 MASK_PAINT_ORDER 逐色遍历，保证「深灰在下、红色在上」（D8）
  for (const color of MASK_PAINT_ORDER) {
    const mask = presentation.masks.find((item) => item.color === color)
    if (!mask) continue
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const key = cellKey(r, c)
        if (!mask.cells.has(key)) continue
        const center = cellCenter(r, c, metrics, presentation.flip)
        layers.push(
          <div key={`mask-${color}-${key}`} style={maskStyle(color, center.x, center.y, cell)} />,
        )
      }
    }
  }

  if (presentation.lastMove) {
    for (const pos of [presentation.lastMove.from, presentation.lastMove.to]) {
      if (!revealed(cellKey(pos.r, pos.c))) continue
      const center = cellCenter(pos.r, pos.c, metrics, presentation.flip)
      layers.push(<div key={`last-${pos.r}-${pos.c}`} style={lastMoveStyle(center.x, center.y, cell)} />)
    }
  }

  if (selected) {
    for (const target of targets) {
      const occupied = presentation.board[target.r][target.c] !== null
      const center = cellCenter(target.r, target.c, metrics, presentation.flip)
      layers.push(
        <div key={`mark-${target.r}-${target.c}`} style={markStyle(center.x, center.y, cell, occupied)} />,
      )
    }
  }

  const boardStyle = {
    position: 'relative',
    width: size.width,
    height: size.height,
    background: '#e3b477',
    borderRadius: 6,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
    touchAction: 'none',
    userSelect: 'none',
    flexShrink: 0,
    ['--cell']: `${cell}px`,
  } as CSSProperties

  return (
    <div
      ref={wrapperRef}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: '1 1 auto',
        minHeight: 0,
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <div
        role="grid"
        aria-label="战争迷雾象棋棋盘"
        aria-rowcount={ROWS}
        aria-colcount={COLS}
        onClick={handleBoardClick}
        style={boardStyle}
      >
        <svg width={size.width} height={size.height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
          <g stroke="#5a3d1e" strokeWidth={1.5} fill="none">
            {Array.from({ length: ROWS }, (_, r) => (
              <line key={`h-${r}`} x1={x(0)} y1={y(r)} x2={x(COLS - 1)} y2={y(r)} />
            ))}
            {Array.from({ length: COLS }, (_, c) =>
              c === 0 || c === COLS - 1 ? (
                <line key={`v-${c}`} x1={x(c)} y1={y(0)} x2={x(c)} y2={y(ROWS - 1)} />
              ) : (
                <g key={`v-${c}`}>
                  <line x1={x(c)} y1={y(0)} x2={x(c)} y2={y(4)} />
                  <line x1={x(c)} y1={y(5)} x2={x(c)} y2={y(ROWS - 1)} />
                </g>
              ),
            )}
            <line x1={x(3)} y1={y(0)} x2={x(5)} y2={y(2)} />
            <line x1={x(5)} y1={y(0)} x2={x(3)} y2={y(2)} />
            <line x1={x(3)} y1={y(7)} x2={x(5)} y2={y(9)} />
            <line x1={x(5)} y1={y(7)} x2={x(3)} y2={y(9)} />
          </g>
          <text x={x(1.7)} y={(y(4) + y(5)) / 2 + 6} fontSize={24} fill="#5a3d1e">
            楚 河
          </text>
          <text x={x(5.2)} y={(y(4) + y(5)) / 2 + 6} fontSize={24} fill="#5a3d1e">
            汉 界
          </text>
        </svg>
        {layers}
        {hitCells}
      </div>
    </div>
  )
}